/**
 * COOKIEBANNER.JS - DSGVO-konformer Cookie-Hinweis mit Opt-In
 *
 * ✅ Opt-In für Analytics-Cookies (DSGVO-konform)
 * ✅ Zwei Optionen: "Alle akzeptieren" oder "Nur notwendige"
 * ✅ Speichert Einwilligung in localStorage
 * ✅ Minimales, nicht-nerviges Design
 *
 * @version 2.0.0 - Opt-In Analytics
 */

import React, { useState, useEffect } from 'react';

function CookieBanner({ onNavigate }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Prüfe ob bereits eine Entscheidung getroffen wurde
    const consent = localStorage.getItem('planivio_cookie_consent');
    if (!consent) {
      // Zeige Banner nach kurzer Verzögerung
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    // Akzeptiert technische + Analytics-Cookies
    localStorage.setItem('planivio_cookie_consent', 'all');
    localStorage.setItem('planivio_analytics_consent', 'accepted');
    localStorage.setItem('planivio_cookies_date', new Date().toISOString());
    setIsVisible(false);

    console.log('✅ Cookie Consent: ALL (Technisch + Analytics)');
  };

  const handleEssentialOnly = () => {
    // Nur technisch notwendige Cookies
    localStorage.setItem('planivio_cookie_consent', 'essential');
    localStorage.setItem('planivio_analytics_consent', 'rejected');
    localStorage.setItem('planivio_cookies_date', new Date().toISOString());
    setIsVisible(false);

    console.log('✅ Cookie Consent: ESSENTIAL ONLY (nur technisch notwendig)');
  };

  const handleMoreInfo = () => {
    // Navigation zu Datenschutz via onNavigate Prop
    if (onNavigate) {
      onNavigate('datenschutz');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .cookie-banner {
          animation: slideUp 0.4s ease-out;
        }
        
        @media (max-width: 600px) {
          .cookie-banner {
            flex-direction: column !important;
            text-align: center !important;
            padding: 20px !important;
          }
          
          .cookie-banner-buttons {
            margin-top: 15px !important;
            width: 100% !important;
            justify-content: center !important;
          }
          
          .cookie-banner-btn {
            flex: 1 !important;
          }
        }
      `}</style>
      
      <div 
        className="cookie-banner"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(15, 23, 42, 0.98)',
          backdropFilter: 'blur(10px)',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          zIndex: 10000,
          borderTop: '1px solid rgba(102, 126, 234, 0.3)',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Text */}
        <div style={{ flex: 1, color: '#e2e8f0', fontSize: '0.9rem', lineHeight: 1.6 }}>
          <span style={{ marginRight: '8px' }}>🍪</span>
          Wir verwenden <strong style={{ color: '#10b981' }}>technisch notwendige Cookies</strong> für die Authentifizierung.
          {' '}
          <strong style={{ color: '#f59e0b' }}>Mit Analytics</strong> helfen Sie uns, Fehler zu finden und
          Planivio zu verbessern - <strong style={{ color: '#a78bfa' }}>100% anonym</strong>.
          {' '}
          <span
            onClick={handleMoreInfo}
            style={{
              color: '#667eea',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Details
          </span>
        </div>

        {/* Buttons */}
        <div
          className="cookie-banner-buttons"
          style={{
            display: 'flex',
            gap: '10px',
            flexShrink: 0
          }}
        >
          <button
            className="cookie-banner-btn"
            onClick={handleEssentialOnly}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '2px solid rgba(148, 163, 184, 0.5)',
              borderRadius: '8px',
              color: '#cbd5e1',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(148, 163, 184, 0.15)';
              e.target.style.borderColor = 'rgba(203, 213, 225, 0.7)';
              e.target.style.color = '#e2e8f0';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = 'rgba(148, 163, 184, 0.5)';
              e.target.style.color = '#cbd5e1';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Nur Notwendige
          </button>

          <button
            className="cookie-banner-btn"
            onClick={handleAcceptAll}
            style={{
              padding: '11px 28px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
            }}
          >
            ✓ Planivio verbessern
          </button>
        </div>
      </div>
    </>
  );
}

export default CookieBanner;